import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { EcosystemAPI, mapServerError, STATE_LABELS, canSeeReviewQueue, reasonCopy } from "../../lib/ecosystemApi";
import { LifecycleChip, EnhancedChip, VersionDot } from "../../components/ecosystem/Chips";
import { EmergencyRevokeDialog, EnhancedApprovalDialog, ReasonDialog } from "../../components/ecosystem/Dialogs";
import { useAuth } from "../../lib/auth";
import { useCapabilities } from "../../lib/useCapabilities";

export default function EntityDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const { caps } = useCapabilities();
  const [tab, setTab] = useState("content");
  const [entity, setEntity] = useState(null);
  const [versions, setVersions] = useState([]);
  const [audit, setAudit] = useState([]);
  const [err, setErr] = useState(null);
  const [banner, setBanner] = useState(null);
  const [showEmergency, setShowEmergency] = useState(false);
  const [showEnhanced, setShowEnhanced] = useState(false);
  const [showReason, setShowReason] = useState(null); // "reject" | "deactivate"

  const load = async () => {
    try {
      const e = await EcosystemAPI.get(id);
      setEntity(e);
      const vs = await EcosystemAPI.versions(id).catch(() => []);
      setVersions(vs);
      const a = await EcosystemAPI.audit(id).catch(() => []);
      setAudit(a);
    } catch (e) { setErr(mapServerError(e)); }
  };

  useEffect(() => { load(); }, [id]);

  const av = useMemo(() => {
    if (!entity) return null;
    // Server-authoritative action set + reason codes.  This component
    // does NOT recompute lifecycle/permission rules — see B.3 contract.
    const actions = new Set(entity.available_actions || []);
    const reasons = entity.action_reasons || {};
    const isEnhanced = !!entity.safety_classification?.enhanced_review;
    return {
      canEdit: actions.has("edit"),
      canSubmit: actions.has("submit"),
      canApprove: actions.has("approve"),
      canReject: actions.has("reject"),
      canResubmit: actions.has("resubmit"),
      canPublish: actions.has("publish"),
      canDeactivate: actions.has("deactivate"),
      canExpire: actions.has("expire"),
      canArchive: actions.has("archive"),
      canUnarchive: actions.has("unarchive"),
      canRetire: actions.has("retire"),
      canEmergencyRevoke: actions.has("emergency_revoke"),
      // The approve affordance stays visible in REVIEW even when the
      // server withholds the action, provided a reason code is set —
      // this preserves the approved disabled+tooltip UX without any
      // client-side authorization.
      approveHasReason: !!reasons.approve,
      approveDisabledReason: reasonCopy(reasons.approve),
      isEnhanced,
    };
  }, [entity]);

  if (err) return <div className="text-sm text-red-700" data-testid="entity-detail-error">{err}</div>;
  if (!entity) return <div className="text-sm text-slate-500">Loading…</div>;

  const call = async (fn, successMsg) => {
    try {
      const updated = await fn();
      setEntity(updated);
      const a = await EcosystemAPI.audit(id).catch(() => []);
      setAudit(a);
      const vs = await EcosystemAPI.versions(id).catch(() => []);
      setVersions(vs);
      if (successMsg) toast.success(successMsg);
    } catch (e) {
      const m = mapServerError(e);
      toast.error(m);
      throw m;
    }
  };

  const enhancedLabel = entity?.safety_classification?.enhanced_review ? "this enhanced-review entity" : null;
  const showReview = canSeeReviewQueue(caps) && ["REVIEW", "REJECTED", "APPROVED"].includes(entity.lifecycle_state);

  const pvVersion = entity.patient_visible_version_id;

  return (
    <div className="space-y-4" data-testid="ecosystem-entity-detail">
      <div className="flex items-center gap-3 flex-wrap">
        <Link to="/ecosystem/entities" className="text-xs text-slate-500 hover:underline">← Entities</Link>
        <h1 className="text-xl font-semibold">{entity.canonical_name}</h1>
        <LifecycleChip state={entity.lifecycle_state} />
        <EnhancedChip classification={entity.safety_classification} />
        <span className="text-xs text-slate-500">v{entity.current_version}</span>
      </div>

      {entity.lifecycle_state === "RETIRED" && (
        <div data-testid="retired-banner" className="p-3 bg-neutral-800 text-neutral-100 rounded">
          This entity is retired. Historical only. No further actions are possible.
        </div>
      )}

      {entity.emergency_revoked_at && (
        <div data-testid="emergency-revoke-banner" className="p-3 bg-red-50 border border-red-200 rounded text-sm">
          <div className="font-semibold text-red-900">Revoked at {entity.emergency_revoked_at}</div>
          <div className="text-slate-700">Reason: {entity.emergency_revoke_reason}</div>
          <div className="text-slate-500 text-xs mt-1">Grounding authorization was invalidated.</div>
        </div>
      )}

      {banner && <div className="p-2 text-sm bg-slate-100 rounded">{banner}</div>}

      <div className="border-b flex gap-4 text-sm" data-testid="entity-detail-tabs">
        <Tab id="content" tab={tab} set={setTab}>Content</Tab>
        <Tab id="versions" tab={tab} set={setTab}>Versions</Tab>
        {showReview && <Tab id="review" tab={tab} set={setTab}>Review</Tab>}
        <Tab id="audit" tab={tab} set={setTab}>Audit</Tab>
      </div>

      {tab === "content" && (
        <ContentTab entity={entity} av={av} pvVersion={pvVersion} onCall={call} user={user}
          onOpenEmergency={() => setShowEmergency(true)}
          onOpenEnhancedApproval={() => setShowEnhanced(true)}
          onOpenReject={() => setShowReason("reject")}
          onOpenDeactivate={() => setShowReason("deactivate")} />
      )}
      {tab === "versions" && <VersionsTab versions={versions} pvVersion={pvVersion} audit={audit} />}
      {tab === "review" && showReview && (
        <ReviewTab entity={entity} audit={audit} />
      )}
      {tab === "audit" && <AuditTab rows={audit} />}

      {av?.canEmergencyRevoke && (
        <EmergencyRevokeDialog
          open={showEmergency}
          onClose={() => setShowEmergency(false)}
          entity={entity}
          onConfirm={async (reason) => call(() => EcosystemAPI.emergencyRevoke(id, reason), "Emergency revoke applied.")}
        />
      )}
      <EnhancedApprovalDialog
        open={showEnhanced}
        onClose={() => setShowEnhanced(false)}
        triggerLabel={enhancedLabel}
        onConfirm={async () => call(() => EcosystemAPI.approve(id), "Enhanced approval recorded.")}
      />
      <ReasonDialog
        open={showReason === "reject"}
        title="Reject this version"
        description="A rejection reason is required. The rejection record is immutable."
        minLength={1}
        submitLabel="Reject"
        onClose={() => setShowReason(null)}
        onConfirm={async (r) => call(() => EcosystemAPI.reject(id, r), "Version rejected.")}
      />
      <ReasonDialog
        open={showReason === "deactivate"}
        title="Deactivate (pause)"
        description="This removes the entity from patient view. It can be reactivated after a new review cycle."
        minLength={1}
        submitLabel="Deactivate"
        onClose={() => setShowReason(null)}
        onConfirm={async (r) => call(() => EcosystemAPI.deactivate(id, r), "Deactivated.")}
      />
    </div>
  );
}

function Tab({ id, tab, set, children }) {
  return (
    <button
      data-testid={`entity-tab-${id}`}
      onClick={() => set(id)}
      className={`py-2 border-b-2 -mb-px ${tab === id ? "border-slate-900 text-slate-900 font-medium" : "border-transparent text-slate-500 hover:text-slate-800"}`}
    >
      {children}
    </button>
  );
}

function ActionBtn({ label, onClick, disabled, disabledReason, testid, variant = "default" }) {
  const base = "px-3 py-1.5 text-sm rounded border";
  const styles = {
    default: `${base} bg-white`,
    primary: `${base} bg-slate-900 text-white border-slate-900 disabled:bg-slate-400 disabled:border-slate-400`,
    danger: `${base} bg-red-700 text-white border-red-700 disabled:bg-red-300 disabled:border-red-300`,
    amber: `${base} bg-amber-600 text-white border-amber-600 disabled:bg-amber-300 disabled:border-amber-300`,
  };
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      disabled={disabled}
      title={disabledReason || undefined}
      className={`${styles[variant]} disabled:cursor-not-allowed`}
    >
      {label}
    </button>
  );
}

function ContentTab({ entity, av, pvVersion, onCall, user, onOpenEmergency, onOpenEnhancedApproval, onOpenReject, onOpenDeactivate }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    canonical_name: entity.canonical_name || "",
    public_description: entity.public_description || "",
    service_area: entity.service_area || "",
  });

  const submitEdit = async () => {
    await onCall(async () => await EcosystemAPI.edit(entity.id, form), "Saved as new version.");
    setEditing(false);
  };

  const pvSummary = pvVersion
    ? `Patients currently see v${pvVersion}.`
    : "No version is currently patient-visible.";
  const workingSummary = `You are editing v${entity.current_version} (${STATE_LABELS[entity.lifecycle_state]}).`;

  return (
    <div className="space-y-4" data-testid="entity-content-tab">
      <div className="p-3 bg-slate-100 rounded text-sm" data-testid="entity-content-context">
        <span className="font-medium">{workingSummary}</span> {pvSummary}
      </div>

      {!editing ? (
        <div className="space-y-2 bg-white p-4 rounded border">
          <Field label="Canonical name" value={entity.canonical_name} />
          <Field label="Public description" value={entity.public_description || "—"} />
          <Field label="Service area" value={entity.service_area || "—"} />
          <Field label="Entity type" value={entity.entity_type} />
          <Field label="Domains" value={(entity.domain_keys || []).join(", ") || "—"} />
        </div>
      ) : (
        <div className="space-y-2 bg-white p-4 rounded border" data-testid="entity-edit-form">
          <label className="text-sm block">Canonical name<input data-testid="edit-name" className="w-full border rounded p-1 text-sm" value={form.canonical_name} onChange={(e) => setForm({ ...form, canonical_name: e.target.value })} /></label>
          <label className="text-sm block">Public description<textarea data-testid="edit-description" className="w-full border rounded p-1 text-sm" rows={3} value={form.public_description} onChange={(e) => setForm({ ...form, public_description: e.target.value })} /></label>
          <label className="text-sm block">Service area<input data-testid="edit-service-area" className="w-full border rounded p-1 text-sm" value={form.service_area} onChange={(e) => setForm({ ...form, service_area: e.target.value })} /></label>
          <div className="flex gap-2 pt-2">
            <button data-testid="ecosystem-version-save" className="px-3 py-1.5 text-sm rounded bg-slate-900 text-white" onClick={submitEdit} title="Editing creates a new immutable version. The previous version remains patient-visible until the new one is approved and published.">Save as new version</button>
            <button className="px-3 py-1.5 text-sm rounded border" onClick={() => setEditing(false)}>Cancel</button>
          </div>
        </div>
      )}

      {!entity.lifecycle_state?.match(/RETIRED/) && (
        <div className="flex flex-wrap gap-2 pt-2" data-testid="entity-actions">
          {av.canEdit && !editing && <ActionBtn testid="ecosystem-edit" label="Edit" onClick={() => setEditing(true)} />}
          {av.canSubmit && <ActionBtn testid="ecosystem-submit" label="Submit for review" variant="primary" onClick={() => onCall(() => EcosystemAPI.submit(entity.id), "Submitted.")} />}
          {av.canResubmit && <ActionBtn testid="ecosystem-resubmit" label="Resubmit" variant="primary" onClick={() => onCall(() => EcosystemAPI.resubmit(entity.id), "Resubmitted.")} />}
          {entity.lifecycle_state === "REVIEW" && (av.canApprove || av.approveHasReason || av.canReject) && (
            <>
              <ActionBtn
                testid="ecosystem-approve"
                label={av.isEnhanced ? "Approve (enhanced)" : "Approve"}
                variant={av.isEnhanced ? "amber" : "primary"}
                disabled={!av.canApprove}
                disabledReason={av.approveDisabledReason}
                onClick={() => av.isEnhanced ? onOpenEnhancedApproval() : onCall(() => EcosystemAPI.approve(entity.id), "Approved.")}
              />
              {av.canReject && <ActionBtn testid="ecosystem-reject" label="Reject" onClick={onOpenReject} />}
            </>
          )}
          {av.canPublish && <ActionBtn testid="ecosystem-publish" label="Publish to patients" variant="primary" onClick={() => onCall(() => EcosystemAPI.publish(entity.id), "Published.")} />}
          {av.canDeactivate && <ActionBtn testid="ecosystem-deactivate" label="Deactivate (pause)" onClick={onOpenDeactivate} />}
          {av.canExpire && <ActionBtn testid="ecosystem-expire" label="Mark expired" onClick={() => onCall(() => EcosystemAPI.expire(entity.id), "Expired.")} />}
          {av.canArchive && <ActionBtn testid="ecosystem-archive" label="Archive" onClick={() => onCall(() => EcosystemAPI.archive(entity.id), "Archived.")} />}
          {av.canUnarchive && <ActionBtn testid="ecosystem-unarchive" label="Unarchive" onClick={() => onCall(() => EcosystemAPI.unarchive(entity.id), "Unarchived.")} />}
          {av.canRetire && <ActionBtn testid="ecosystem-retire" label="Retire (terminal)" variant="danger" onClick={() => onCall(() => EcosystemAPI.retire(entity.id), "Retired.")} />}
        </div>
      )}

      {av.canEmergencyRevoke && (
        <details className="mt-4 border rounded" data-testid="emergency-actions-panel">
          <summary className="p-2 text-sm text-red-800 cursor-pointer bg-red-50">Emergency actions</summary>
          <div className="p-3">
            <ActionBtn testid="ecosystem-emergency-revoke" label="Emergency revoke — remove from patients now" variant="danger" onClick={onOpenEmergency} />
          </div>
        </details>
      )}

      {entity.lifecycle_state === "PATIENT_VISIBLE" && entity.patient_visible_version_id && (
        <GroundingPanel entity={entity} user={user} onCall={onCall} />
      )}
    </div>
  );
}

function GroundingPanel({ entity, user, onCall }) {
  const [reason, setReason] = React.useState("");
  const { caps } = useCapabilities();
  const canAuthorize = !!caps.ecosystem_grounding_authorize;
  const version = entity.patient_visible_version_id;
  return (
    <div className="mt-4 border rounded p-3 bg-slate-50" data-testid="grounding-panel">
      <div className="text-sm font-semibold text-slate-900">Ask Northstar grounding · v{version}</div>
      <div className="text-xs text-slate-600 mt-1">
        Grounding is a separate governance decision from patient visibility.
        A version becomes retrievable by Ask Northstar only after an
        enhanced reviewer or admin explicitly authorises it. New versions
        never inherit grounding.
      </div>
      <input
        data-testid="grounding-reason-input"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder="Reason (required for reject/revoke)"
        className="mt-2 w-full text-sm border rounded p-1"
      />
      <div className="flex flex-wrap gap-2 pt-2" data-testid="grounding-actions">
        <ActionBtn
          testid="grounding-request-btn"
          label="Request grounding review"
          onClick={() => onCall(() => EcosystemAPI.groundingRequest(entity.id, version, reason || null), "Grounding review requested.")}
        />
        {canAuthorize && (
          <ActionBtn
            testid="grounding-authorize-btn"
            label="Authorize grounding"
            variant="primary"
            onClick={() => onCall(() => EcosystemAPI.groundingAuthorize(entity.id, version, reason || null), "Grounding authorized.")}
          />
        )}
        {canAuthorize && (
          <ActionBtn
            testid="grounding-reject-btn"
            label="Reject grounding"
            onClick={() => reason ? onCall(() => EcosystemAPI.groundingReject(entity.id, version, reason), "Grounding rejected.") : toast.error("Reason required to reject.")}
          />
        )}
        {canAuthorize && (
          <ActionBtn
            testid="grounding-revoke-btn"
            label="Revoke grounding"
            variant="danger"
            onClick={() => reason ? onCall(() => EcosystemAPI.groundingRevoke(entity.id, version, reason), "Grounding revoked.") : toast.error("Reason required to revoke.")}
          />
        )}
      </div>
    </div>
  );
}

function Field({ label, value }) {
  return <div className="text-sm"><span className="text-slate-500">{label}: </span><span className="text-slate-900">{value}</span></div>;
}

function VersionsTab({ versions, pvVersion, audit }) {
  // Determine per-version approval / rejection outcomes from audit.
  const outcomes = {};
  audit.forEach((a) => {
    if (a.event_type === "entity_approved_standard" || a.event_type === "entity_approved_enhanced") {
      outcomes[a.after?.version || a.before?.version] = "approved";
    }
    if (a.event_type === "entity_rejected") {
      outcomes[a.after?.version || a.before?.version] = "rejected";
    }
  });
  const sorted = [...versions].sort((a, b) => a.version - b.version);
  return (
    <ul className="space-y-2" data-testid="entity-versions-tab">
      {sorted.map((v) => {
        const kind =
          v.version === pvVersion ? "patient-visible" :
          outcomes[v.version] === "rejected" ? "rejected" :
          outcomes[v.version] === "approved" ? "approved" : "historical";
        return (
          <li key={v.version} data-testid={`version-row-${v.version}`} className="bg-white p-3 rounded border flex items-center gap-3">
            <VersionDot kind={kind} />
            <span className="font-medium">v{v.version}</span>
            <span className={`text-xs ${kind === "rejected" ? "line-through text-slate-400" : "text-slate-500"}`}>
              {kind === "patient-visible" ? "Patient-visible" : kind === "approved" ? "Approved" : kind === "rejected" ? "Rejected" : "Historical"}
            </span>
            <span className="text-xs text-slate-400 ml-auto">{v.created_at}</span>
          </li>
        );
      })}
    </ul>
  );
}

function ReviewTab({ entity, audit }) {
  const rejections = audit.filter((a) => a.event_type === "entity_rejected");
  return (
    <div className="space-y-2" data-testid="entity-review-tab">
      {entity.lifecycle_state === "REVIEW" && <div className="p-3 bg-sky-50 border border-sky-200 rounded text-sm">This entity is in review at v{entity.current_version}.</div>}
      <h3 className="text-sm font-medium text-slate-600">Rejection history</h3>
      {rejections.length === 0 && <div className="text-sm text-slate-500">No rejections recorded.</div>}
      <ul className="space-y-2">
        {rejections.map((r) => (
          <li key={r.id} className="bg-white p-3 rounded border text-sm">
            <div>v{r.before?.version ?? r.after?.version} — rejected by <span className="font-medium">{r.actor_email_at_time}</span> ({r.actor_role_at_time})</div>
            <div className="text-slate-600 text-xs mt-1">{r.change_summary}</div>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function AuditTab({ rows }) {
  return (
    <ul className="space-y-2" data-testid="entity-audit-tab">
      {rows.length === 0 && <div className="text-sm text-slate-500">No audit events yet.</div>}
      {rows.map((r) => (
        <li key={r.id} data-testid={`audit-row-${r.id}`} className="bg-white p-3 rounded border text-sm">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-slate-500">{r.occurred_at}</span>
            <span className="font-medium">{r.event_type}</span>
            {r.enhanced_review && (
              <span data-testid="audit-enhanced-chip" className="text-xs px-1.5 py-0.5 rounded-full border border-amber-400 bg-amber-50 text-amber-900">Enhanced</span>
            )}
          </div>
          <div className="text-xs text-slate-600">
            {r.actor_email_at_time} ({r.actor_role_at_time}) — {r.change_summary}
          </div>
          {r.enhanced_review && r.enhanced_triggers && (
            <div className="text-xs text-amber-800 mt-1">
              Triggers: {(r.enhanced_triggers.domains || []).join(", ")}{(r.enhanced_triggers.categories || []).length ? " · " + r.enhanced_triggers.categories.join(", ") : ""}
            </div>
          )}
        </li>
      ))}
    </ul>
  );
}
